import { useCallback, useEffect, useRef, useState } from 'react';
import agent from '../../../services/api/agent';
import { addCanonicalGzipMetadataToMp3Blob } from '../../../utils/mp3Metadata';
import { GoogleAiVoiceDraftV1, GoogleSynthesisRequest } from './types';
import { buildGoogleSynthesisRequest, getGoogleTtsInputLimitError } from './utils/googleTtsRequest';
import { canonicalizeGoogleDraft } from './utils/canonicalizeGoogleDraft';

interface UseGoogleTTSReturn {
  fetchGoogleTTS: (
    draft: GoogleAiVoiceDraftV1,
    courseID: string | number,
    pageID: string | number,
    metadataDraft?: GoogleAiVoiceDraftV1,
  ) => Promise<void>;
  audioUrl: string;
  audioDuration: number | null;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  audioFile: File | null;
}

const useGoogleTTS = (): UseGoogleTTSReturn => {
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const lastAudioUrlRef = useRef<string | null>(null);

  const revokeLastAudioUrl = useCallback(() => {
    if (!lastAudioUrlRef.current) return;
    URL.revokeObjectURL(lastAudioUrlRef.current);
    lastAudioUrlRef.current = null;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchGoogleTTS = useCallback(
    async (draft: GoogleAiVoiceDraftV1, courseID: string | number, pageID: string | number, metadataDraft?: GoogleAiVoiceDraftV1) => {
      setLoading(true);
      setError(null);
      setAudioDuration(null);
      setAudioFile(null);
      setAudioUrl('');
      revokeLastAudioUrl();

      try {
        const requestedAudioEncoding: NonNullable<GoogleSynthesisRequest['audioConfig']>['audioEncoding'] = 'MP3';
        // Keep metadata canonical aligned with persisted page draft, not transformed synthesis payload.
        const canonical = canonicalizeGoogleDraft(metadataDraft ?? draft);
        const requestPayload = buildGoogleSynthesisRequest(draft, requestedAudioEncoding);

        const inputLimitError = getGoogleTtsInputLimitError(requestPayload);
        if (inputLimitError) {
          setError(inputLimitError);
          return;
        }

        const audioData = await agent.Proxy.GoogleTTS(requestPayload);
        const mimeType = audioData.type?.trim() || 'audio/mpeg';
        const isMp3 = mimeType.toLowerCase().includes('mpeg') || mimeType.toLowerCase().includes('mp3');

        let blobToUse: Blob = audioData;
        if (isMp3) {
          try {
            blobToUse = await addCanonicalGzipMetadataToMp3Blob(audioData, canonical, { courseID, pageID });
          } catch {
            blobToUse = audioData;
          }
        }

        const fileExtension = mimeType.toLowerCase().includes('wav') ? 'wav' : 'mp3';
        const fileName = `${courseID}_${pageID}_aivoice.${fileExtension}`;
        const file = new File([blobToUse], fileName, { type: mimeType });
        const nextAudioUrl = URL.createObjectURL(blobToUse);
        lastAudioUrlRef.current = nextAudioUrl;

        const duration = await new Promise<number | null>((resolve) => {
          const audioElement = new Audio(nextAudioUrl);

          const cleanup = () => {
            audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audioElement.removeEventListener('error', handleError);
          };

          const handleLoadedMetadata = () => {
            const value = Number.isFinite(audioElement.duration) ? audioElement.duration : null;
            cleanup();
            resolve(value);
          };

          const handleError = () => {
            cleanup();
            resolve(null);
          };

          audioElement.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
          audioElement.addEventListener('error', handleError, { once: true });
        });

        setAudioDuration(duration);
        setAudioUrl(nextAudioUrl);
        setAudioFile(file);
      } catch (fetchError) {
        setError(fetchError instanceof Error ? fetchError.message : 'Unable to generate audio.');
      } finally {
        setLoading(false);
      }
    },
    [revokeLastAudioUrl],
  );

  useEffect(() => revokeLastAudioUrl, [revokeLastAudioUrl]);

  return { fetchGoogleTTS, audioUrl, audioDuration, loading, error, clearError, audioFile };
};

export default useGoogleTTS;
